import { Component } from '@angular/core';
import { OnInit, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AlertsService } from '../../services/alerts.service';
import { AlbumsService } from '../../services/albums.service';
import { UserService } from '../../services/user.service';

/**
 * Component for managing items in an album.
 */
@Component({
  selector: 'album-item',
  templateUrl: './album-item.component.html',
  styleUrl: './album-item.component.css'
})
export class AlbumItemComponent implements OnInit {
	@Input() albumItems: any;

	LOCALIZATION: any;
	
	albums: any[] = [];
	records: any[] = [];
	productAlbums: any[] = [];
	
	mode: string = 'add';
	albumcreate: boolean = false;
	currentAlbum: number = 0;
	selectedAlbum: any;
	input = {
	    albumname: '',
	    pagename: ''
	};
	limits = {
	    albumname: 255
	};
	fallbackImage: string = '/app/aw-ui-angular/assets/img/noimage.webp';
	inprogress: boolean = false;	
	
	constructor(
		private userService: UserService,
		private alertsService: AlertsService,
		private albumsService: AlbumsService,
		private ngbmodalService: NgbActiveModal
	) { }

	ngOnInit(): void {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.records = this.albumItems.records;
		this.mode = this.albumItems.mode;		
		this.loadAlbums();
	}
	
	/**
	 * Loads the albums for the current user.
	 */
	loadAlbums(selectedId?: number): void {
		this.albumsService.albumslist(true).then(
			(albumData: any) => {

				let handleRecord = (record: any) => {
					let id = record.uuid || record.id;

					this.albumsService.getAlbumsForProduct(id, record.page).then(
						(response: any) => {
							// Filter albums that belong to the current user and add to productAlbums if not already set
							response.forEach((productAlbum: any) => {
								if (productAlbum.userId === this.userService.user.id) {
									productAlbum.selected = false;
									let exists = this.productAlbums.some((album: any) => album.id === productAlbum.id);
									if (!exists) this.productAlbums.push(productAlbum);
								}
							});

							// Select the album if selectedId is provided
							if (selectedId) {
								let selectedIndex = albumData.findIndex((album: any) => album[0] === selectedId);
								if (selectedIndex !== -1) {
									this.currentAlbum = selectedIndex;
								}
							}

							this.albums = albumData;
						},
						(err: any) => {
							console.error("Failed to fetch albums for product %s", id, err);
							this.productAlbums = [];
							this.albums = [];
						}
					);
				};

				this.records.forEach(handleRecord);
			},
			(err: any) => {
				console.error("Failed to fetch albums", err);
				this.albums = [];
			}
		);
	}
	
	selectAlbum(index: number): void {
		this.currentAlbum = index;
	}
	
	/**
	 * Adds items to an album.
	 */	
	addPage(): void {
		if (!this.albums[this.currentAlbum]) {
			this.alertsService.error({
				message: this.LOCALIZATION.addAlbumItemNoSelectionError
			});
			return;
		}

		this.inprogress = true;
		let id = this.albums[this.currentAlbum][0];
		let errors: { record: any, msg: string }[] = [];
		let successes: { record: any, msg: string }[] = [];

		let promises = this.records.map(record => {
			record.pagename = this.input.pagename;

			// Return the promise from the service call
			return this.albumsService.addAlbumProduct(id, record)
				.then((response: any) => {
					successes.push({
						record: record,
						msg: "The document was added to the album. Updates will appear after indexing is complete."
					});
				})
				.catch((err: any) => {
					let msg = err.status === 409
						? "The document already exists in the album."
						: "Could not add the document to the album.";
					errors.push({ record: record, msg: msg });
				});
		});

		Promise.all(promises).then(() => {
			console.log('All albums processed');
			
			this.inprogress = false;
			
			if (errors.length > 0) {
				let uniqueMessages = new Set(errors.map(error => error.msg));
				let concatenatedMessages = Array.from(uniqueMessages).join(' ')
								
				this.alertsService.error({
				    message: this.LOCALIZATION.addAlbumItemError + concatenatedMessages
				});
				this.close();				
			} else {
				this.alertsService.success({
				    message: this.LOCALIZATION.addAlbumItemSuccess
				});
				this.close();				
			}
		});
	}
	
	/**
	 * Removes items from an album.
	 */	
	removePage(): void {
		if (this.selectedAlbum) {
			let idsArray = this.records.map(function(record: any) {
				return record.uuid;
			});

			this.albumsService.deleteAlbumProducts(this.selectedAlbum.id, idsArray).then(
				(response: any) => {
					this.alertsService.success({
						message: this.LOCALIZATION.deleteAlbumItemSuccess
					});
					this.close();
				},
				(err: any) => {
					console.error("Failed to remove album item", err);
					this.alertsService.error({
						message: this.LOCALIZATION.deleteAlbumItemError
					});
					this.close();
				}
			);
		}
	}

	/**
	 * Creates a new album.
	 */
	addAlbum(): void {
		if (!this.input.albumname) {
	        this.alertsService.error({
	            message: "Please provide an album name."
	        });
	        return;
		}
		
	    this.albumsService.addAlbum(this.input.albumname, '').then(
			(newAlbumId: any) => {
		        this.alertsService.success({
		            message: "The album was created."
		        });
		        this.input.albumname = "";
		        this.loadAlbums(newAlbumId);
		        this.albumcreate = false;
		        return;
		    }, 
			(err: any) => {
				console.error("Failed to create album", err);
		        this.alertsService.error({
		            message: "Could not create the album."
		        });
		        return;
		    }
		);		
	}

	handleAddClick(): void {
	    if (this.albumcreate) {
	        this.addAlbum();
	    } else {
	        this.addPage();
	    }		
	}

	handleRemoveClick(): void {
		this.albumcreate = false;
		this.removePage();
	}

	handleCloseClick(): void {
	    if (this.albumcreate) {
	        this.albumcreate = false;
	    } else {
	        this.close();
	    }		
	}
	
	onKeyup(event: KeyboardEvent): void {
	  if (event.keyCode === 13) {
	    this.addAlbum();
	  }
	}	

	onImageError(event: Event): void {
		let imgElement = event.target as HTMLImageElement;
		imgElement.src = this.fallbackImage;
	}

	/**
	 * Closes the modal.
	 */
	close(): void {
		this.ngbmodalService.dismiss();
	}
}
